import {
    getCanonicalItemList,
    isCanonicalItemList
} from './data-layer-constants';

export const CART_ATTRIBUTION_KEY_V1 = 'upi.analytics.cart-attribution.v1';
export const PRODUCT_SELECTION_KEY_V1 = 'upi.analytics.product-selection.v1';
export const CART_ATTRIBUTION_KEY = 'upi.analytics.cart-attribution.v2';
export const PRODUCT_SELECTION_KEY = 'upi.analytics.product-selection.v2';

function getDefaultStorage ( storageName ) {
    try {
        return window[storageName];
    }
    catch ( error ) {
        return null;
    }
}

function getDefaultScope () {
    try {
        return window.upi?.User?.info?.orderId;
    }
    catch ( error ) {
        return null;
    }
}

function normalizeScope ( scope ) {
    const normalizedScope = String( scope || '' ).trim().toLowerCase();
    return normalizedScope || null;
}

function readPayload ( key, storage ) {
    if ( !storage ) {
        return null;
    }

    try {
        const payload = JSON.parse( storage.getItem( key ) || 'null' );
        return payload && typeof payload === 'object' && !Array.isArray( payload ) ? payload : null;
    }
    catch ( error ) {
        return null;
    }
}

function removeStorageItem ( storage, key ) {
    try {
        storage?.removeItem( key );
    }
    catch ( error ) {
        console.warn( '[Analytics Attribution] Unable to update browser storage', error );
    }
}

function readScopedRecords ( key, legacyKey, storage, scope ) {
    if ( !storage ) {
        return {};
    }

    removeStorageItem( storage, legacyKey );
    const normalizedScope = normalizeScope( scope );
    const payload = readPayload( key, storage );
    const records = payload?.records;

    if ( !normalizedScope || payload?.scope !== normalizedScope || !records ||
        typeof records !== 'object' || Array.isArray( records ) ) {
        removeStorageItem( storage, key );
        return {};
    }

    return records;
}

function writeScopedRecords ( key, storage, scope, records ) {
    if ( !storage ) {
        return;
    }

    const normalizedScope = normalizeScope( scope );
    try {
        if ( !normalizedScope || Object.keys( records ).length === 0 ) {
            removeStorageItem( storage, key );
            return;
        }
        storage.setItem( key, JSON.stringify( {scope: normalizedScope, records} ) );
    }
    catch ( error ) {
        console.warn( '[Analytics Attribution] Unable to update browser storage', error );
    }
}

function getSku ( item ) {
    return item?.product?.sku || item?.sku || item?.itemId || item?.item_id;
}

function readCanonicalRecord ( records, sku ) {
    const record = records[sku];
    return record ? getCanonicalItemList( record.id, record.name ) : null;
}

export function synchronizeCommerceAttributionScope (
    cartStorage = getDefaultStorage( 'localStorage' ),
    selectionStorage = getDefaultStorage( 'sessionStorage' ),
    scope = getDefaultScope(),
    {preserveCartAttribution = false} = {}
) {
    if ( !preserveCartAttribution ) {
        readScopedRecords( CART_ATTRIBUTION_KEY, CART_ATTRIBUTION_KEY_V1, cartStorage, scope );
    }
    readScopedRecords( PRODUCT_SELECTION_KEY, PRODUCT_SELECTION_KEY_V1, selectionStorage, scope );
}

export function rememberProductSelection (
    item,
    itemList,
    storage = getDefaultStorage( 'sessionStorage' ),
    scope = getDefaultScope()
) {
    const sku = getSku( item );
    if ( !sku || !isCanonicalItemList( itemList ) ) {
        return;
    }

    const records = readScopedRecords( PRODUCT_SELECTION_KEY, PRODUCT_SELECTION_KEY_V1, storage, scope );
    records[sku] = itemList;
    writeScopedRecords( PRODUCT_SELECTION_KEY, storage, scope, records );
}

export function applyProductSelectionAttribution (
    lineItems,
    storage = getDefaultStorage( 'sessionStorage' ),
    scope = getDefaultScope()
) {
    const records = readScopedRecords( PRODUCT_SELECTION_KEY, PRODUCT_SELECTION_KEY_V1, storage, scope );
    return lineItems.map( lineItem => {
        if ( isCanonicalItemList( lineItem.itemList ) ) {
            return lineItem;
        }

        const itemList = readCanonicalRecord( records, getSku( lineItem ) );
        return itemList ? {...lineItem, itemList} : lineItem;
    } );
}

export function removeProductSelections (
    items,
    storage = getDefaultStorage( 'sessionStorage' ),
    scope = getDefaultScope()
) {
    const records = readScopedRecords( PRODUCT_SELECTION_KEY, PRODUCT_SELECTION_KEY_V1, storage, scope );
    items.forEach( item => delete records[getSku( item )] );
    writeScopedRecords( PRODUCT_SELECTION_KEY, storage, scope, records );
}

export function rememberCartAttributions (
    lineItems,
    storage = getDefaultStorage( 'localStorage' ),
    scope = getDefaultScope()
) {
    const records = readScopedRecords( CART_ATTRIBUTION_KEY, CART_ATTRIBUTION_KEY_V1, storage, scope );

    lineItems.forEach( lineItem => {
        const sku = getSku( lineItem );
        if ( sku && !readCanonicalRecord( records, sku ) && isCanonicalItemList( lineItem.itemList ) ) {
            records[sku] = lineItem.itemList;
        }
    } );

    writeScopedRecords( CART_ATTRIBUTION_KEY, storage, scope, records );
}

export function getCartAttribution (
    sku,
    storage = getDefaultStorage( 'localStorage' ),
    scope = getDefaultScope()
) {
    return readCanonicalRecord(
        readScopedRecords( CART_ATTRIBUTION_KEY, CART_ATTRIBUTION_KEY_V1, storage, scope ),
        sku
    );
}

export function enrichProductsWithCartAttribution (
    products,
    storage = getDefaultStorage( 'localStorage' ),
    scope = getDefaultScope()
) {
    const records = readScopedRecords( CART_ATTRIBUTION_KEY, CART_ATTRIBUTION_KEY_V1, storage, scope );
    return products.map( product => {
        const itemList = readCanonicalRecord( records, getSku( product ) );
        return itemList ? {...product, itemList} : product;
    } );
}

export function removeCartAttributions (
    items,
    storage = getDefaultStorage( 'localStorage' ),
    scope = getDefaultScope()
) {
    const records = readScopedRecords( CART_ATTRIBUTION_KEY, CART_ATTRIBUTION_KEY_V1, storage, scope );
    items.forEach( item => delete records[getSku( item )] );
    writeScopedRecords( CART_ATTRIBUTION_KEY, storage, scope, records );
}

export function clearCartAttributions (
    storage = getDefaultStorage( 'localStorage' ),
    scope = getDefaultScope()
) {
    readScopedRecords( CART_ATTRIBUTION_KEY, CART_ATTRIBUTION_KEY_V1, storage, scope );
    writeScopedRecords( CART_ATTRIBUTION_KEY, storage, scope, {} );
}
